// src/components/events/EventDetail/EventDetailMetadata.tsx
// Event 詳情元數據區：Start/End Time、All Day Toggle、Duration、Location

import React, { useState } from 'react';
import { Event, EventTimeType } from '../../../../types/calendar-event';
import { Clock, MapPin, CalendarClock, Sun } from '../../../../components/common/icons';
import MetadataRow from '../../../../components/tasks/MetadataRow';
import SimpleDatePicker from '../../../../components/common/SimpleDatePicker';
import TimePickerModal from '../../../../components/common/TimePickerModal';
import { combineDateTime, deconstructDateTime, calculateDuration } from '../../../../utils/timeBoxHelpers';
import { formatDurationFromMinutes } from '../../../../utils/formatters';
import { useOnboarding, TipTooltip } from '../../../onboarding';
import { eventBus, EVENTS } from '../../../../utils/eventBus';

interface EventDetailMetadataProps {
  event: Event;
  onStartTimeChange: (startTime: Date) => Promise<void>;
  onEndTimeChange: (endTime: Date) => Promise<void>;
  onTimeTypeChange: (timeType: EventTimeType) => Promise<void>;
  onLocationChange: (location: string | undefined) => Promise<void>;
  variant: 'desktop' | 'mobile';
}

interface ModalStates {
  startDatePicker: boolean;
  startTimePicker: boolean;
  endDatePicker: boolean;
  endTimePicker: boolean;
}

export const EventDetailMetadata: React.FC<EventDetailMetadataProps> = ({
  event,
  onStartTimeChange,
  onEndTimeChange,
  onTimeTypeChange,
  onLocationChange,
  variant,
}) => {
  const isMobile = variant === 'mobile';
  const showLabelOnMobile = isMobile;
  const isAllDay = event.timeType === 'allday';
  const { shouldShowTip, markTipAsSeen } = useOnboarding();

  // Modal 狀態管理
  const [modals, setModals] = useState<ModalStates>({
    startDatePicker: false,
    startTimePicker: false,
    endDatePicker: false,
    endTimePicker: false,
  });

  // 暫存選中的日期（用於分步驟流程）
  const [tempStartDate, setTempStartDate] = useState<string>('');
  const [tempEndDate, setTempEndDate] = useState<string>('');

  // 解構時間
  const { dateStr: startDateStr, timeStr: startTimeStr } = deconstructDateTime(event.startTime);
  const { dateStr: endDateStr, timeStr: endTimeStr } = deconstructDateTime(event.endTime);

  // 計算時長
  const duration = formatDurationFromMinutes(
    calculateDuration(event.startTime, event.endTime)
  );

  // 日期快捷選項（今天、明天、週末）
  const getQuickDateOptions = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const dayOfWeek = today.getDay();
    const daysUntilSaturday = dayOfWeek === 0 ? 6 : (6 - dayOfWeek);
    const thisSaturday = new Date(today);
    thisSaturday.setDate(today.getDate() + daysUntilSaturday);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return [
      { value: formatDate(today), label: 'Today' },
      { value: formatDate(tomorrow), label: 'Tomorrow' },
      { value: formatDate(thisSaturday), label: 'This weekend' },
    ];
  };

  // Modal 管理輔助函數
  const openModal = (modal: keyof ModalStates) => {
    setModals((prev) => ({ ...prev, [modal]: true }));
  };

  const closeModal = (modal: keyof ModalStates) => {
    setModals((prev) => ({ ...prev, [modal]: false }));
  };

  // 格式化時間顯示
  const formatTimeDisplay = (date: Date) => {
    if (isAllDay) {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // 處理開始時間選擇流程
  const handleStartTimeClick = () => {
    openModal('startDatePicker');
  };

  const handleStartDateSelected = (dateStr: string) => {
    if (isAllDay) {
      // All Day 模式：選日期後直接更新
      const newStartTime = combineDateTime(dateStr, '00:00');
      onStartTimeChange(newStartTime);
      closeModal('startDatePicker');
    } else {
      // Timed 模式：選日期後進入時間選擇
      setTempStartDate(dateStr);
      closeModal('startDatePicker');
      openModal('startTimePicker');
    }
  };

  const handleStartTimeSelected = async (timeStr: string) => {
    const newStartTime = combineDateTime(tempStartDate, timeStr);
    await onStartTimeChange(newStartTime);
    closeModal('startTimePicker');
    setTempStartDate('');
  };

  const handleStartTimeBack = () => {
    closeModal('startTimePicker');
    openModal('startDatePicker');
  };

  // 處理結束時間選擇流程
  const handleEndTimeClick = () => {
    openModal('endDatePicker');
  };

  const handleEndDateSelected = (dateStr: string) => {
    if (isAllDay) {
      // All Day 模式：選日期後直接更新
      const newEndTime = combineDateTime(dateStr, '23:59');
      onEndTimeChange(newEndTime);
      closeModal('endDatePicker');
    } else {
      setTempEndDate(dateStr);
      closeModal('endDatePicker');
      openModal('endTimePicker');
    }
  };

  const handleEndTimeSelected = async (timeStr: string) => {
    const newEndTime = combineDateTime(tempEndDate, timeStr);
    await onEndTimeChange(newEndTime);
    closeModal('endTimePicker');
    setTempEndDate('');
  };

  const handleEndTimeBack = () => {
    closeModal('endTimePicker');
    openModal('endDatePicker');
  };

  // 計算 End Time 的 minTime 限制（僅 Timed 模式）
  const getEndTimeMinTime = (): string | undefined => {
    // All Day 模式不限制時間
    if (isAllDay) {
      return undefined;
    }

    // 只有當 End Date 與 Start Date 同天時才限制
    if (tempEndDate !== startDateStr) {
      return undefined;
    }

    // 計算 Start Time + 15 分鐘作為最小時間
    const [startHour, startMinute] = startTimeStr.split(':').map(Number);
    const startTotalMinutes = startHour * 60 + startMinute;
    const minTotalMinutes = startTotalMinutes + 15;

    // 防止跨午夜溢出（23:45 之後）
    if (minTotalMinutes >= 24 * 60) {
      return undefined; // 超過 23:45，不限制（讓用戶選跨日）
    }

    const minHour = Math.floor(minTotalMinutes / 60);
    const minMinute = minTotalMinutes % 60;

    return `${String(minHour).padStart(2, '0')}:${String(minMinute).padStart(2, '0')}`;
  };

  // All Day Toggle
  const handleAllDayToggle = () => {
    // 發佈 UI 事件用於 Tip 自動標記
    eventBus.publish(EVENTS.UI.ALLDAY_TOGGLED);
    onTimeTypeChange(isAllDay ? 'timed' : 'allday');
  };

  const containerClass = isMobile
    ? 'md:hidden mt-6 px-4'
    : 'hidden md:flex md:w-52 md:shrink-0 md:flex-col';

  const innerClass = isMobile
    ? 'space-y-0'
    : 'metadata-sidebar sticky top-4 flex-1';

  return (
    <div className={containerClass}>
      {/* Mobile 分隔線 */}
      {isMobile && <div className="meta-divider mb-4" />}

      <div className={innerClass}>
        {/* All Day Toggle - 獨立行 */}
        <div className="relative meta-row -mx-2 px-2">
          <Sun className="w-[18px] h-[18px] shrink-0 text-gray-500" />
          {showLabelOnMobile && (
            <span className="md:hidden w-24 text-sm text-gray-500 font-normal text-left shrink-0">All Day</span>
          )}
          <span className="meta-value flex-1 text-sm text-gray-600">
            {!isMobile && 'All Day'}
          </span>
          <button
            onClick={handleAllDayToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-sky-500
              ${isAllDay ? 'bg-yellow-400' : 'bg-gray-300'}`}
            role="switch"
            aria-checked={isAllDay}
            aria-label={isAllDay ? 'Disable all-day mode' : 'Enable all-day mode'}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                ${isAllDay ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>

          {/* 首次使用提示 */}
          {shouldShowTip('event-allday-toggle') && (
            <TipTooltip
              tipId="event-allday-toggle"
              position="bottom"
              arrowAlign="end"
              onClose={() => markTipAsSeen('event-allday-toggle')}
            />
          )}
        </div>

        {/* Start Time */}
        <MetadataRow
          icon={CalendarClock}
          label="Start"
          value={formatTimeDisplay(event.startTime)}
          onClick={handleStartTimeClick}
          showLabelOnMobile={showLabelOnMobile}
        />

        {/* End Time */}
        <MetadataRow
          icon={CalendarClock}
          label="End"
          value={formatTimeDisplay(event.endTime)}
          onClick={handleEndTimeClick}
          showLabelOnMobile={showLabelOnMobile}
        />

        {/* Duration (只讀) - All Day 時灰化顯示 "--" */}
        <div className={isAllDay ? 'opacity-40' : ''}>
          <MetadataRow
            icon={Clock}
            label="Duration"
            value={isAllDay ? '--' : duration}
            showLabelOnMobile={showLabelOnMobile}
          />
        </div>

        <div className="meta-divider" />

        {/* Location */}
        <MetadataRow
          icon={MapPin}
          label="Location"
          value={event.location}
          inlineEdit={{
            // Event 的 onLocationChange 仍為 string | undefined 介面，待統一後移除此轉換
            onSave: (value) => onLocationChange(value ?? undefined),
            placeholder: 'Location',
            maxLength: 100,
          }}
          showLabelOnMobile={showLabelOnMobile}
        />

        <div className="meta-divider" />

        {/* Created (只讀) */}
        <div className="meta-row supplementary">
          <span className="meta-label">Created</span>
          <span className="meta-value">
            {event.createdAt
              ? new Date(event.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'N/A'}
          </span>
        </div>
      </div>

      {/* Modals - Start Time Flow */}
      {modals.startDatePicker && (
        <SimpleDatePicker
          currentValue={startDateStr}
          onSelect={handleStartDateSelected}
          onClose={() => closeModal('startDatePicker')}
          hideNoDate={true}
          customQuickOptions={getQuickDateOptions()}
        />
      )}

      {modals.startTimePicker && (
        <TimePickerModal
          title="Select Start Time"
          currentValue={startTimeStr}
          onSelect={handleStartTimeSelected}
          onClose={() => closeModal('startTimePicker')}
          onBack={handleStartTimeBack}
          interval={15}
          use24Hours={false}
          pickerMode="wheel"
        />
      )}

      {/* Modals - End Time Flow */}
      {modals.endDatePicker && (
        <SimpleDatePicker
          currentValue={endDateStr}
          minDate={startDateStr}
          onSelect={handleEndDateSelected}
          onClose={() => closeModal('endDatePicker')}
          hideNoDate={true}
          customQuickOptions={getQuickDateOptions()}
        />
      )}

      {modals.endTimePicker && (
        <TimePickerModal
          title="Select End Time"
          currentValue={endTimeStr}
          minTime={getEndTimeMinTime()}
          onSelect={handleEndTimeSelected}
          onClose={() => closeModal('endTimePicker')}
          onBack={handleEndTimeBack}
          interval={15}
          use24Hours={false}
          pickerMode="wheel"
        />
      )}
    </div>
  );
};
